import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import { Button, Field, Input, Modal } from '@/components/ui';

/**
 * Change your own password, from the account menu on every page.
 *
 * The form existed only deep inside client Settings, where nobody found it
 * ("there is no option to change password for clients"). The account menu is
 * where people look. Other sessions are signed out by the server; this one
 * stays signed in.
 */
export function ChangePasswordModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const reset = () => {
    setCurrent('');
    setNext('');
    setConfirm('');
    setError(null);
    setDone(false);
  };

  const change = useMutation({
    mutationFn: () => api.post('/auth/change-password', { current_password: current, new_password: next }),
    onSuccess: () => {
      setDone(true);
      setError(null);
    },
    onError: (err) => setError(errorMessage(err, 'Could not change the password')),
  });

  const problem =
    next && next.length < 12
      ? 'At least 12 characters.'
      : confirm && confirm !== next
        ? 'The two new passwords do not match.'
        : null;

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Change password"
      description="Your other devices are signed out; this one stays signed in."
      footer={
        done ? (
          <Button
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Done
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={() => change.mutate()}
              loading={change.isPending}
              disabled={!current || next.length < 12 || next !== confirm}
            >
              Change password
            </Button>
          </>
        )
      }
    >
      {done ? (
        <p className="text-sm text-emerald-700 dark:text-emerald-400">Your password has been changed.</p>
      ) : (
        <div className="space-y-4">
          <Field label="Current password">
            <Input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
          </Field>
          <Field label="New password" hint="At least 12 characters." error={next && next.length < 12 ? problem ?? undefined : undefined}>
            <Input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
          </Field>
          <Field label="Repeat new password" error={confirm && confirm !== next ? 'The two new passwords do not match.' : undefined}>
            <Input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </Field>
          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        </div>
      )}
    </Modal>
  );
}
